import { useMemo } from "react";
import { DatasetListItem, DatasetListResponse } from "datasets/types/interfaces/datasetListItem";
import useSWR from "swr";
import { DATASETS_API_PREFIX, LIST_DESCRIPTION_MAX_LENGTH } from "~/services/catalog";
import { apiClient } from "~/utils/apiClient";

/** Most ids the v1 list's `id` filter accepts in one request. This matches the API's limit. */
const MAX_IDS_PER_REQUEST = 100;

/**
 * Resolve dataset ids to v1 list records, in the order given. Ids the caller can't
 * see (deleted, access revoked, personal) are left out. More ids than one request
 * accepts are split across parallel requests.
 */
export function useDatasetsById(ids: string[] | undefined) {
  // Keeping the previous key's data stops the list flashing back to loading when an
  // id is attached or removed, which changes the key.
  const { data, error } = useSWR(datasetsByIdKey(ids), fetchDatasetsById, {
    keepPreviousData: true,
  });

  const datasets = useMemo<DatasetListItem[] | undefined>(() => {
    if (ids === undefined) {
      return undefined;
    }
    if (ids.length === 0) {
      return [];
    }
    if (data === undefined) {
      return undefined;
    }
    const byId = new Map(data.items.map((d) => [d.id, d]));
    return ids.map((id) => byId.get(id)).filter((d): d is DatasetListItem => Boolean(d));
  }, [ids, data]);

  return { data: datasets, error };
}

// One list URL per chunk of ids, or no request at all for no ids. Sorted so
// reordering the same ids keeps the key.
function datasetsByIdKey(ids: string[] | undefined): string[] | null {
  if (!ids?.length) {
    return null;
  }
  const sorted = [...new Set(ids)].sort();
  const urls: string[] = [];
  for (let start = 0; start < sorted.length; start += MAX_IDS_PER_REQUEST) {
    const chunk = sorted.slice(start, start + MAX_IDS_PER_REQUEST);
    const idParams = chunk.map((id) => `id=${encodeURIComponent(id)}`).join("&");
    urls.push(
      `${DATASETS_API_PREFIX}?offset=0&limit=${chunk.length}&description_max_length=${LIST_DESCRIPTION_MAX_LENGTH}&${idParams}`
    );
  }
  return urls;
}

// Merged into a list-shaped response so `deleteDataset`'s cache update applies here too.
async function fetchDatasetsById(urls: string[]): Promise<Pick<DatasetListResponse, "items">> {
  const pages = await Promise.all(urls.map((url) => apiClient<DatasetListResponse>(url)));
  return { items: pages.flatMap((page) => page.items) };
}
